<template>
  <main>
    <div class="l-flex-row--between onboarding-cclone-header">
      <sectionHeader :title="'Cclone'">
        <template #title>
          <span>Cclone</span>
        </template>
      </sectionHeader>
      <el-dropdown trigger="click" placement="bottom-start" @command="handleCommand">
        <button class="btn-border btn--round l-flex-row l-align-center" style="height: 34px; padding: 0 8px">
          CClone Actions
          <svg-icon class="sub-navigation-cta-icon is-white" name="dots" size="small" style="margin-right: -5px;" />
        </button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item command="export" :disabled="disabledCcloneExport" class="l-flex-row dropdown-menu-action">
            <span>CClone export</span>
          </el-dropdown-item>
          <el-dropdown-item v-if="isCccloneActionAllowed" command="new" class="l-flex-row dropdown-menu-action">
            <span>New CClone</span>
          </el-dropdown-item>
          <template v-if="selectedCcloneId !== null && isCccloneActionAllowed">
            <el-dropdown-item divided />
            <el-dropdown-item command="edit" v-if="ccloneInfo.status === 'failed' || ccloneInfo.status === 'waiting'" class="l-flex-row dropdown-menu-action" :disabled="disabledCcloneAction || !criteriaListIsReady">
              <span>Edit selected cclone</span>
            </el-dropdown-item>
            <el-dropdown-item command="delete" :disabled="disabledCcloneAction" class="l-flex-row dropdown-menu-action">
              <span>Delete selected cclone</span>
            </el-dropdown-item>
          </template>
        </el-dropdown-menu>
      </el-dropdown>
    </div>
    <Table
      class="onboarding-cclone-table"
      :full-data="ccloneList"
      :table-data="tableData"
      :select-row="setSelectedRow"
      :selected-row-id="selectedCcloneId"
      :set-active-tab="setActiveTab"
      default-tab="information"
      :display-search="true"
      :search-filters="searchFilters"
      search-place-holder="Search cclones by label or id"
      :display-filters="true"
      :filters-data="filtersData"
      :filter-dates-keys="['Activation', 'End', 'cclones']"
      :date-filters="dateFilters"
      :default-dates="defaultDates"
      :filter-date-operators="filterDateOperators"
      style="margin-top: 55px"
    />
  </main>
</template>

<script>
import dayjs from 'dayjs'
import { formatDate } from '@/utils/helpers'
import { mapState, mapGetters, mapMutations } from 'vuex'
import { cloneDeep, isEmpty } from 'lodash'
import { CCLONE_TABLE_PROPS, CCLONE_FILTERS_PROPS, CCLONE_SEARCH_PROPS, FILTER_DATE_OPERATORS, FILTERING_DEFAULT_DATES } from '@/utils/constants'

import Table from '@/components/common/Table'
import SectionHeader from '@/components/common/SectionHeader'
import { Dropdown, DropdownItem, DropdownMenu } from 'element-ui'

export default {
    name: 'CcloneMonitoring',

    components: {
        Table,
        SectionHeader,
        'el-dropdown': Dropdown,
        'el-dropdown-menu': DropdownMenu,
        'el-dropdown-item': DropdownItem,
    },

    mounted() {
        this.dateFilters = [
            {
                icon: { name: 'calendar', size: 'small' },
                dates: cloneDeep(this.filterCreatedDate),
                key: 'filterCreatedDate',
                from: {
                    label: 'Created',
                    placeholder: this.createdOrStartDateFromDefaultValue,
                    pickerOptions: this.pickerOptionsCreatedFrom,
                },
                to: {
                    label: 'to',
                    placeholder: this.newDate,
                    pickerOptions: this.pickerOptionsStartOrCreatedTo
                }
            },
            {
                icon: { name: 'calendar', size: 'small' },
                dates: cloneDeep(this.filterStartDate),
                key: 'filterStartDate',
                from: { label: 'Activation', placeholder: this.createdOrStartDateFromDefaultValue, pickerOptions: this.pickerOptionsStartFrom },
                to: {
                    label: 'to',
                    placeholder: this.activationDatePlaceholder,
                    defaultValue: this.activationDatePlaceholder,
                    pickerOptions: this.pickerOptionsStartOrCreatedTo
                }
            },
            {
                icon: { name: 'calendar', size: 'small' },
                dates: cloneDeep(this.filterEndDate),
                key: 'filterEndDate',
                from: { label: 'End', placeholder: this.newDate, pickerOptions: null },
                to: { label: 'to', placeholder: this.endDatePlaceholder, defaultValue: this.endDatePlaceholder, pickerOptions: null }
            }
        ]
    },

    data() {
        return {
            displayMainMenu: false,
            tableData: {
                config: CCLONE_TABLE_PROPS.config,
                pagination: {
                    currentPage: CCLONE_TABLE_PROPS.pagination.currentPage,
                    currentPageSize: CCLONE_TABLE_PROPS.pagination.rowPerPage
                },
                sorting: CCLONE_TABLE_PROPS.sorting,
                opened: []
            },

            // SEARCH
            searchFilters: cloneDeep(CCLONE_SEARCH_PROPS),

            // FILTERS
            filtersData: {
                filters: cloneDeep(CCLONE_FILTERS_PROPS),
                filterTargets: { bootstrap: '', status: '', type: '', shared: '' }
            },

            // DATE FILTERS
            dateFilters: [],
            filterDateOperators: FILTER_DATE_OPERATORS,
            filterCreatedDate: cloneDeep(FILTERING_DEFAULT_DATES.created),
            filterStartDate: cloneDeep(FILTERING_DEFAULT_DATES.start),
            filterEndDate: cloneDeep(FILTERING_DEFAULT_DATES.end),
            defaultDates: cloneDeep(FILTERING_DEFAULT_DATES)
        }
    },

    computed: {
        ...mapGetters('data/cclone', ['getFormattedCclones', 'getItemById']),
        ...mapState('ui/cclone', {
            selectedCcloneId: state => state.selectedRowId
        }),
        ...mapGetters('ui/builder', ['criteriaListIsReady']),

        ...mapGetters('account', [
            'getAccessToCclone',
            'getIsCcloneActionAllowed',
            'getIsCcloneReadAllowed',
        ]),

        // TABLE
        ccloneList() {
            return this.getFormattedCclones()
        },

        // DATE FILTERS
        newDate() {
            return formatDate(new Date())
        },

        activationDatePlaceholder() {
            return formatDate(dayjs().add(90, 'day'))
        },

        endDatePlaceholder() {
            return formatDate(dayjs().add(120, 'day'))
        },
        createdOrStartDateFromDefaultValue() {
            return formatDate(dayjs(this.newDate))
        },
        pickerOptionsStartFrom() {
            const threeMonth = dayjs().add(90, 'day').startOf('day').valueOf()
            return {
                disabledDate(time) {
                    return time ? time.getTime() >= threeMonth : null
                }
            }
        },
        pickerOptionsCreatedFrom() {
            const _filterCreatedDate = this.filterCreatedDate
            if (this.disabledCreatedPeriodFilter) {
                return {
                    disabledDate(time) {
                        return time ? time.getTime() > Date.now() : null
                    }
                }
            } else {
                return {
                    disabledDate(time) {
                        return time ? time.getTime() > _filterCreatedDate.to.getTime() : null
                    }
                }
            }
        },

        disabledCcloneAction() {
            return !this.getIsCcloneReadAllowed()
        },
        isCccloneActionAllowed() {
            return this.getIsCcloneActionAllowed()
        },
        disabledCcloneExport() {
            return isEmpty(this.getFormattedCclones())
        },
        ccloneInfo() {
            const _info = this.getItemById({ id: this.selectedCcloneId })
            // @TODO error notification
            if (_info === false) return null
            return _info
        }
    },

    methods: {
        // Call to mutations
        ...mapMutations('ui/cclone', ['setSelectedRow', 'setActiveTab']),
        ...mapMutations('ui/cclone', { displayCcloneModal: 'setDisplayModal', setIsEditing: 'setIsEditing' }),
        ...mapMutations('exports', ['setExportForm']),

        handleCommand(command) {
            if (command === 'export') {
                this.exportDataForm('cclone')
                return
            }

            if (command === 'new') {
                this.newCclone()
                return
            }

            if (command === 'edit') {
                this.editCclone()
                return
            }

            if (command === 'delete') {
                this.deleteModalCclone()
                return
            }
        },

        // Nav CTA
        toggleMainActionMenu() {
            this.displayMainMenu = !this.displayMainMenu
        },


        editCclone() {
            this.setIsEditing({ isEditing: true })
            this.displayCcloneModal({ modal: 'ccloneCreation', isDisplayed: true })
        },

        // CCLONE
        newCclone() {
            this.displayCcloneModal({ modal: 'ccloneCreation', isDisplayed: true })
        },

        // FORM - MODAL
        exportDataForm(type) {
            this.setExportForm({
                display: true,
                type
            })
        },

        deleteModalCclone() {
            this.displayCcloneModal({ modal: 'cclonedelete', isDisplayed: true })
        },
    }
}
</script>

<style>
.dropdown-menu-action span {
  font-size: 1rem;
}
.onboarding-cclone-header {
  align-items: center; height: 34px
}
</style>
